/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  McpSubscription,
  McpPlan,
  CreateMcpSubscriptionPayload,
  UpdateMcpSubscriptionPayload,
} from './types';
import { createMcpSubscription, updateMcpSubscription } from './mcpSubscriptionService';

interface McpSubscriptionFormDialogProps {
  open: boolean;
  subscription: McpSubscription | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const PLANS: McpPlan[] = ['BASIC', 'PRO', 'ENTERPRISE'];

const McpSubscriptionFormDialog: React.FC<McpSubscriptionFormDialogProps> = ({
  open,
  subscription,
  onClose,
  onSuccess,
  onError,
}) => {
  const isEdit = Boolean(subscription);

  const [clientId, setClientId] = useState('');
  const [plan, setPlan] = useState<McpPlan>('BASIC');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [maxToolCalls, setMaxToolCalls] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (subscription) {
      setClientId(String(subscription.clientId));
      setPlan(subscription.plan);
      setStartDate(dayjs(subscription.startDate));
      setEndDate(subscription.endDate ? dayjs(subscription.endDate) : null);
      setMaxToolCalls(String(subscription.maxToolCalls));
    } else {
      setClientId('');
      setPlan('BASIC');
      setStartDate(dayjs());
      setEndDate(null);
      setMaxToolCalls('');
    }
  }, [subscription, open]);

  const isValid = () => {
    const cid = Number(clientId);
    const mtc = Number(maxToolCalls);
    return cid > 0 && startDate && mtc > 0;
  };

  const handleSubmit = () => {
    if (!isValid()) return;

    setSubmitting(true);

    const formattedStartDate = startDate!.format('YYYY-MM-DD');
    const formattedEndDate = endDate ? endDate.format('YYYY-MM-DD') : null;

    if (isEdit && subscription) {
      const payload: UpdateMcpSubscriptionPayload = {
        id: subscription.id,
        clientId: Number(clientId),
        plan,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        maxToolCalls: Number(maxToolCalls),
      };
      updateMcpSubscription(payload)
        .then(() => {
          onSuccess('Subscription updated successfully.');
          onClose();
        })
        .catch(() => {
          onError('Failed to update subscription.');
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      const payload: CreateMcpSubscriptionPayload = {
        clientId: Number(clientId),
        plan,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        maxToolCalls: Number(maxToolCalls),
      };
      createMcpSubscription(payload)
        .then(() => {
          onSuccess('Subscription created successfully.');
          onClose();
        })
        .catch(() => {
          onError('Failed to create subscription.');
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit MCP Subscription' : 'Create MCP Subscription'}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Client ID"
              type="number"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel id="mcp-plan-label">Plan</InputLabel>
              <Select
                labelId="mcp-plan-label"
                value={plan}
                label="Plan"
                onChange={(e) => setPlan(e.target.value as McpPlan)}
              >
                {PLANS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
            <DatePicker
              label="End Date (optional)"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TextField
              label="Max Tool Calls"
              type="number"
              value={maxToolCalls}
              onChange={(e) => setMaxToolCalls(e.target.value)}
              required
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !isValid()}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default McpSubscriptionFormDialog;
